import { useAuthStore } from '@/auth';

/**
 * String variant of the clinic label for use in native `<option>` elements
 * (which can't hold JSX). Returns just the name for non-internal viewers
 * and "Name (#id)" when the viewer is RxFood internal. Caller passes the
 * same `isInternal` they read once at the top of the component — that's
 * cheaper than re-subscribing inside a hot loop.
 */
export const clinicOptionLabel = (
  name: string,
  id: number | string | null | undefined,
  isInternal: boolean,
): string => {
  if (!isInternal || id == null) return name;
  return `${name} (#${id})`;
};

/** Hook wrapper around the `is_internal` flag for one-line gating elsewhere. */
export const useIsInternalUser = (): boolean =>
  !!useAuthStore((s) => s.clinician?.is_internal);

/**
 * Renders a clinic's display name, with the numeric id appended in muted
 * text when the viewing clinician is an RxFood internal user (`is_internal`
 * flag from the auth-store clinician profile).
 *
 *   Acme Clinic              (regular clinician)
 *   Acme Clinic (#42)        (internal RxFood employee)
 *
 * Drop-in for `<span>{clinic.name}</span>` anywhere a clinic appears in a
 * dropdown / list / row. The id-on-hover affordance also lives on the
 * title attribute so internal users can copy without selecting through the
 * muted suffix.
 */
type Props = {
  name: string;
  id?: number | string | null;
  className?: string;
};

export const ClinicLabel = ({ name, id, className }: Props) => {
  const isInternal = useIsInternalUser();
  if (!isInternal || id == null) {
    return <span className={className}>{name}</span>;
  }
  return (
    <span className={className} title={`#${id}`}>
      {name}
      <span className="ml-1 text-xs font-normal text-muted-foreground">(#{id})</span>
    </span>
  );
};
